import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import "./About.css";

const About = () => {
  // Refs for each section for parallax effect
  const visionRef = useRef(null);
  const philosophyRef = useRef(null);
  const teamRef = useRef(null);

  // Parallax effect for Vision section
  const { scrollYProgress: scrollYVision } = useScroll({
    target: visionRef,
    offset: ["start end", "end start"],
  });
  const visionY = useTransform(scrollYVision, [0, 1], [0, -200]);

  // Parallax effect for Philosophy section
  const { scrollYProgress: scrollYPhilosophy } = useScroll({
    target: philosophyRef,
    offset: ["start end", "end start"],
  });
  const philosophyY = useTransform(scrollYPhilosophy, [0, 1], [0, -200]);

  // Animation variants for text
  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const teamMembers = [
    {
      name: "Dr. Arez",
      role: "CEO & Founder",
      image: "images/arez.png", // You'll add these images later
    },
    {
      name: "Ali",
      role: "Creative Director",
      image: "images/ali.png",
    },
    {
      name: "Shaida",
      role: "Digital Director",
      image: "images/shaida.png",
    },
    {
      name: "Goran",
      role: "Sales Director",
      image: "images/goran.png",
    },
  ];

  // Container variants for team section
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  // Item variants for team members
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <main id="main-content">
      {/* Vision Section */}
      <section ref={visionRef} className="about-section vision-section">
        <motion.div
          className="about-background"
          style={{ y: visionY }}
        ></motion.div>
        <div className="about-container">
          <motion.div
            className="about-content"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.h1 className="about-title" variants={textVariants}>
              <span className="yellow-gradient-text">About Kynetic</span>
            </motion.h1>
            <motion.h2 className="about-subtitle" variants={textVariants}>
              <span className="yellow-gradient-text">Our Vision</span>
            </motion.h2>
            <motion.h3 className="about-kicker" variants={textVariants}>
              Driving Directional Force
            </motion.h3>
            <motion.p className="about-description" variants={textVariants}>
              In a digital landscape defined by noise, 'Kynetic' represents
              clear, directional force. We started this agency to solve a
              specific problem: great brands getting stuck in static strategies.
              We exist to break the cycle and propel your business toward its
              revenue goals.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section ref={philosophyRef} className="about-section philosophy-section">
        <motion.div
          className="about-background"
          style={{ y: philosophyY }}
        ></motion.div>
        <div className="about-container philosophy-container">
          <motion.div
            className="about-content"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.h2 className="about-title" variants={textVariants}>
              <span className="yellow-gradient-text">Our Philosophy</span>
            </motion.h2>
            <motion.p className="about-description" variants={textVariants}>
              The Physics of Growth Marketing is simply physics. It requires the
              right amount of force applied in the right direction. We strip
              away the guesswork and apply a proven framework of testing,
              learning, and scaling. We don't believe in luck; we believe in
              calculated impact.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Team Section */}
      <section ref={teamRef} className="team-section">
        <div className="team-container">
          <motion.h2
            className="team-title"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Meet the minds{" "}
            <span className="yellow-text">moving the needle</span>
          </motion.h2>

          <motion.div
            className="team-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            {teamMembers.map((member, index) => (
              <motion.div
                key={index}
                className="team-member"
                variants={itemVariants}
              >
                <div className="team-member-image-container">
                  <div
                    className="team-member-image"
                    role="img"
                    aria-label={`${member.name}, ${member.role}`}
                    style={{ backgroundImage: `url(${member.image})` }}
                  ></div>
                  <div className="team-member-overlay"></div>
                </div>
                <div className="team-member-info">
                  <h3 className="team-member-name">{member.name}</h3>
                  <p className="team-member-role">{member.role}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default About;
